import { useState } from 'react'
import { AlertTriangle, Check, ChevronDown, ChevronRight, XCircle } from 'lucide-react'

import { cn } from '../../lib/utils'

/**
 * Detects + parses the synthetic-resume instruction the UI POSTs to
 * `/agent-orchestrator` after every row in a `propose_cypher_writes`
 * plan settles. The format is produced by `buildResumeInstruction` in
 * `domains/ontology-ui/state/src/agents/cypher-write-resume.ts`:
 *
 *   [user decisions on proposal plan-XYZ]
 *   1. APPLIED — "<summary>" (write aw-…); nodesCreated=…, ….
 *   2. REJECTED — "<summary>" (write aw-…); reason: "<reason>".
 *   3. APPLY_FAILED — "<summary>" (write aw-…); error: <err>.
 *
 *   Please confirm what was applied, …
 *
 * The hydrate-on-load fallback emits a shorter "N proposal(s) … were
 * resolved while I was offline." line; we surface that as a single
 * "Resolved while offline" pill.
 *
 * Rendering rationale: showing the raw instruction in the chat
 * transcript looks like the user typed a wall of agent metadata, which
 * is jarring. The bubble below collapses to a one-line summary
 * ("Approved 8 changes, rejected 3") and lets the user click to expand
 * the per-row detail.
 */
const HEADER_RE = /^\[user decisions on proposal (plan-[a-zA-Z0-9_-]+)\]/
const HYDRATE_OFFLINE_HINT = 'were resolved (applied or rejected) while I was offline'

interface ParsedDecision {
  number: number
  kind: 'applied' | 'rejected' | 'apply_failed' | 'undecided'
  summary: string
  writeId: string | null
  /** Comma-joined `nodesCreated=…, …` body for applied rows; null otherwise. */
  appliedCounts: string | null
  rejectionReason: string | null
  applyFailedError: string | null
}

export interface ParsedResumeInstruction {
  planId: string
  decisions: ParsedDecision[]
  /**
   * True when the instruction is the hydrate-on-load fallback emitted
   * by `dispatchUnresumedCypherPlans` — we don't have per-row detail,
   * just the planSize and a "resolved while offline" framing.
   */
  hydrateFallback: boolean
  fallbackCount: number | null
}

const HYDRATE_COUNT_RE = /The (\d+) proposal\(s\)/

export function parseResumeInstruction(text: string): ParsedResumeInstruction | null {
  const trimmed = text.trim()
  const match = HEADER_RE.exec(trimmed)
  if (!match) return null
  const planId = match[1]

  if (trimmed.includes(HYDRATE_OFFLINE_HINT)) {
    const countMatch = HYDRATE_COUNT_RE.exec(trimmed)
    const fallbackCount = countMatch ? Number.parseInt(countMatch[1], 10) : null
    return {
      planId,
      decisions: [],
      hydrateFallback: true,
      fallbackCount: Number.isFinite(fallbackCount as number) ? fallbackCount : null
    }
  }

  // Body: every line starting with "<n>. <KIND> — …".
  const lineRe =
    /^(\d+)\.\s+(APPLIED|REJECTED|APPLY_FAILED|UNDECIDED)\s+—\s+"([^"]*)"(?:\s*\(write\s+([^)]+)\))?\s*(?:;\s*(.*?))?\.?\s*$/
  const decisions: ParsedDecision[] = []
  for (const rawLine of trimmed.split(/\r?\n/)) {
    const line = rawLine.trim()
    const m = lineRe.exec(line)
    if (!m) continue
    const [, num, kindRaw, summary, writeRaw, tail] = m
    const kind: ParsedDecision['kind'] =
      kindRaw === 'APPLIED'
        ? 'applied'
        : kindRaw === 'REJECTED'
          ? 'rejected'
          : kindRaw === 'APPLY_FAILED'
            ? 'apply_failed'
            : 'undecided'
    let appliedCounts: string | null = null
    let rejectionReason: string | null = null
    let applyFailedError: string | null = null
    if (tail) {
      const cleanTail = tail.replace(/\.\s*$/, '').trim()
      if (kind === 'applied') {
        appliedCounts = cleanTail.length > 0 ? cleanTail : null
      } else if (kind === 'rejected') {
        const r = /^reason:\s*"(.*)"$/.exec(cleanTail)
        rejectionReason = r ? r[1] : cleanTail.replace(/^reason:\s*/, '').replace(/^"|"$/g, '')
      } else if (kind === 'apply_failed') {
        applyFailedError = cleanTail.replace(/^error:\s*/, '')
      }
    }
    decisions.push({
      number: Number.parseInt(num, 10),
      kind,
      summary,
      writeId: writeRaw ? writeRaw.trim() : null,
      appliedCounts,
      rejectionReason,
      applyFailedError
    })
  }

  return {
    planId,
    decisions,
    hydrateFallback: false,
    fallbackCount: null
  }
}

interface DecisionTotals {
  applied: number
  rejected: number
  applyFailed: number
  undecided: number
}

function tallyDecisions(decisions: ParsedDecision[]): DecisionTotals {
  const totals: DecisionTotals = { applied: 0, rejected: 0, applyFailed: 0, undecided: 0 }
  for (const d of decisions) {
    if (d.kind === 'applied') totals.applied += 1
    else if (d.kind === 'rejected') totals.rejected += 1
    else if (d.kind === 'apply_failed') totals.applyFailed += 1
    else totals.undecided += 1
  }
  return totals
}

function buildSummaryLine(
  parsed: ParsedResumeInstruction,
  totals: DecisionTotals,
  total: number
): string {
  if (parsed.hydrateFallback) {
    if (parsed.fallbackCount && parsed.fallbackCount > 1) {
      return `Resolved ${parsed.fallbackCount} proposals while offline`
    }
    return 'Resolved proposal while offline'
  }

  if (total === 0) return 'No decisions recorded'

  const noun = total === 1 ? 'change' : 'changes'

  // Single-bucket cases read nicest as "Approved 11 changes" /
  // "Rejected 3 changes" / "1 change failed".
  if (totals.applied === total) {
    return `Approved ${totals.applied} ${noun}`
  }
  if (totals.rejected === total) {
    return `Rejected ${totals.rejected} ${noun}`
  }
  if (totals.applyFailed === total) {
    return `${totals.applyFailed} ${noun} failed to apply`
  }

  // Mixed outcome: comma-join the populated buckets and tack the noun
  // on to the end ("Approved 8, rejected 3 changes").
  const parts: string[] = []
  if (totals.applied > 0) parts.push(`approved ${totals.applied}`)
  if (totals.applyFailed > 0) parts.push(`${totals.applyFailed} failed`)
  if (totals.rejected > 0) parts.push(`rejected ${totals.rejected}`)
  const joined = parts.join(', ')
  // Capitalise the first word.
  const sentence = joined.charAt(0).toUpperCase() + joined.slice(1)
  return `${sentence} (of ${total} ${noun})`
}

const COUNT_LABELS: Record<string, [string, string]> = {
  nodesCreated: ['node added', 'nodes added'],
  nodesModified: ['node updated', 'nodes updated'],
  nodesDeleted: ['node removed', 'nodes removed'],
  relationshipsCreated: ['relationship added', 'relationships added'],
  relationshipsDeleted: ['relationship removed', 'relationships removed']
}

/**
 * Turn the machine-facing `nodesModified=1, ...` applied-counts string the
 * resume instruction carries into human prose ("1 node updated"). Falls back
 * to the raw segment for any key we don't have a label for.
 */
function prettifyAppliedCounts(raw: string): string {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return ''
  if (trimmed === 'no graph changes') return 'No graph changes'
  const parts: string[] = []
  for (const segment of trimmed.split(',')) {
    const [key, value] = segment.split('=').map((s) => s.trim())
    const n = Number.parseInt(value ?? '', 10)
    const labels = COUNT_LABELS[key]
    if (!labels || !Number.isFinite(n)) {
      parts.push(segment.trim())
      continue
    }
    parts.push(`${n} ${n === 1 ? labels[0] : labels[1]}`)
  }
  return parts.join(', ')
}

function HeadlineIcon({
  totals,
  hydrateFallback
}: {
  totals: DecisionTotals
  hydrateFallback: boolean
}) {
  if (hydrateFallback) {
    return <Check className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
  }
  if (totals.applyFailed > 0) {
    return <AlertTriangle className="size-3.5 shrink-0 text-destructive" aria-hidden />
  }
  if (totals.applied > 0 && totals.rejected === 0) {
    return (
      <Check className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
    )
  }
  if (totals.applied === 0 && totals.rejected > 0) {
    return <XCircle className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
  }
  return <Check className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
}

/**
 * Compact replacement for the raw synthetic-resume instruction in the
 * chat transcript. Renders as a small primary-tinted bubble (so it
 * still reads as "from the user") with an expandable per-row detail
 * list.
 */
export function ResumeDecisionsBubble({ parsed }: { parsed: ParsedResumeInstruction }) {
  const [open, setOpen] = useState(false)
  const totals = tallyDecisions(parsed.decisions)
  const total = parsed.decisions.length
  const summary = buildSummaryLine(parsed, totals, total)
  const hasDetails = parsed.decisions.length > 0

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-line/70 bg-muted/30 text-13 shadow-sm duration-300 animate-in fade-in slide-in-from-bottom-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!hasDetails}
        aria-expanded={open}
        title={
          hasDetails ? (open ? 'Click to collapse details' : 'Click to expand details') : undefined
        }
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-foreground transition-colors duration-200 ease-out hover:bg-muted/50 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <HeadlineIcon totals={totals} hydrateFallback={parsed.hydrateFallback} />
        <span className="flex-1 break-words font-medium">{summary}</span>
        {hasDetails ? (
          <ChevronDown
            aria-hidden
            className={cn(
              'size-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ease-out',
              open ? 'rotate-0' : '-rotate-90'
            )}
          />
        ) : null}
      </button>
      <div
        data-open={open && hasDetails}
        className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out data-[open=true]:grid-rows-[1fr]"
      >
        <div className="min-h-0 overflow-hidden">
          <ul
            data-open={open && hasDetails}
            className="flex flex-col gap-0.5 border-t border-line/60 px-2 py-1.5 text-12 opacity-0 transition-opacity duration-200 ease-out data-[open=true]:opacity-100"
          >
            {parsed.decisions.map((d) => (
              <li key={d.number} className="flex min-w-0 items-start gap-2 rounded-md px-1.5 py-1">
                <DecisionRowIcon kind={d.kind} />
                <div className="min-w-0 flex-1">
                  <p className="break-words text-foreground">
                    <span className="text-muted-foreground tabular-nums">{d.number}.</span>{' '}
                    {d.summary}
                  </p>
                  {d.kind === 'applied' && d.appliedCounts ? (
                    <p className="text-11 text-emerald-600 dark:text-emerald-400">
                      {prettifyAppliedCounts(d.appliedCounts)}
                    </p>
                  ) : null}
                  {d.kind === 'rejected' && d.rejectionReason ? (
                    <p className="text-11 text-muted-foreground">Reason: {d.rejectionReason}</p>
                  ) : null}
                  {d.kind === 'apply_failed' && d.applyFailedError ? (
                    <p className="text-11 text-destructive">{d.applyFailedError}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function DecisionRowIcon({ kind }: { kind: ParsedDecision['kind'] }) {
  if (kind === 'applied') {
    return (
      <Check
        className="mt-0.5 size-3 shrink-0 text-emerald-600 dark:text-emerald-400"
        aria-hidden
      />
    )
  }
  if (kind === 'apply_failed') {
    return <AlertTriangle className="mt-0.5 size-3 shrink-0 text-destructive" aria-hidden />
  }
  if (kind === 'rejected') {
    return <XCircle className="mt-0.5 size-3 shrink-0 text-muted-foreground" aria-hidden />
  }
  return <ChevronRight className="mt-0.5 size-3 shrink-0 text-muted-foreground" aria-hidden />
}
